import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import { CustomPracticeProfileFieldEnum } from 'src/models/GalenData/CustomField.model';
import { GalenErrorKeys } from 'src/models/GalenData/GalenError.model';
import { convertPracticeSettings } from 'src/models/GalenData/helpers/saveSettings';
import { getLocaleCode, Language } from 'src/models/Language.model';
import { Session } from 'src/models/Session.model';

import useGetGalenErrorMsg from 'src/hooks/useGetGalenErrorMsg';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useUiTranslation from 'src/hooks/useUiTranslation';

export const useHandleViewReport = () => {
  const { tCommon, language } = useUiTranslation();

  const { getErrorMessage } = useGetGalenErrorMsg();
  const { handleHttpRequestError } = useHandleHttpRequestError();

  const {
    userSettings: { userPermission },
    currentUser,
    setPracticeSettings,
    setLogo,
  } = useRemoteBoundStore();

  const practiceId = currentUser?.currentRole?.practice?.practiceId;

  const handleViewReport = async (session: Session): Promise<Session> => {
    try {
      const allowed = userPermission.read;

      if (!allowed) {
        throw new Error(tCommon('message.error.noPermissionViewReport'));
      }

      if (!practiceId) {
        throw new Error(getErrorMessage(GalenErrorKeys.PRACTICE_IS_REQUIRED));
      }

      const practiceSettings = await Api.remote.getPracticeSettings(practiceId);

      const practiceSettingsData = convertPracticeSettings(
        practiceSettings.data,
      );

      setPracticeSettings({
        practiceSettings: practiceSettingsData,
      });

      const logoId = practiceSettings.data.find(
        (setting) =>
          setting.field.name === CustomPracticeProfileFieldEnum.LogoFile,
      )?.fieldData;

      if (logoId) {
        const logo = await Api.remote.getLogoFile({
          deviceDataId: logoId,
        });

        if (logo.data.Logo) {
          setLogo({ logo: logo.data.Logo.link });
        } else {
          setLogo({ logo: '' });
        }
      }

      return {
        ...session,
        template: session.order?.OrderType.value,
      };
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);

      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: `${tCommon('message.error.failedToViewReport')} ${errorMessage}`,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });

      throw error;
    }
  };

  return { handleViewReport };
};
